/**
 * Coups de pouce — attendu 651 (problèmes d'horaires : multi-étapes + tableau de bus).
 *
 * VARIANTES routées par contenu.sousType (structurel, posé par la branche du
 * gen : 'etapes' | 'bus') — même mécanique que 650. Décisions revue 2026-08-01 :
 *   - fiche étapes ANIMÉE (demande utilisateur) : le calcul en chaîne
 *     18 h 50 → 18 h 85 (trop grand) → conversion 19 h 25 → 20 h 37 ✓ ;
 *   - fiche bus ANIMÉE : le geste « barrer les bus déjà partis » est visuel
 *     par nature, vraie valeur ajoutée du SVG ;
 *   - trous guidés : page UNIQUE dédiée à la conversion — l'exercice
 *     échafaude déjà l'addition étape par étape (« Étape 1 : … = {{h}} h
 *     {{mm}} »), le travail résiduel de l'élève est la conversion quand les
 *     minutes dépassent 59. L'animation avant/après de 650 (21 h 72 →
 *     22 h 12) est réutilisée telle quelle (une seule source de vérité).
 * Valeurs d'exemples HORS pools : départs à 18 h+ (le gen tire 13 h-17 h),
 * heure actuelle bus à 20 h (le gen tire 7 h-19 h), durées non multiples de 5.
 */

import cp650 from './650';

// L'animation de conversion vit en page 2 de la fiche trous guidés de 650.
const SVG_CONVERSION_650 = cp650.calcul_horaire_texte_trou_saisie.pages[1].svg;

const FICHE_CONVERSION = {
    pages: [
        {
            titre: 'Quand les minutes dépassent 59',
            lignes: [
                'Ajoute les heures avec les heures, les minutes avec les minutes. Si le total des **minutes** dépasse **59**, enlève **60 min** et ajoute **1 h**.',
            ],
            svg: SVG_CONVERSION_650,
        },
    ],
};

// Barre animée sur le bus déjà parti, cadre sur le prochain : même motif CSS
// que les droites de 650 (fenêtres d'opacité décalées, fallback statique).
const SVG_TABLEAU_BUS = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 182" width="290" height="176" role="img"
     aria-label="Il est 20 h 12. Tableau de quatre bus dans le désordre : celui de 20 h 04 est barré car déjà parti ; celui de 20 h 26, en première ligne, est encadré : c'est le prochain, avant ceux de 20 h 33 et 20 h 51 ; l'attente est de 14 minutes">
  <style>
    .anim { opacity: 1; }
    @media (prefers-reduced-motion: no-preference) {
      .b1 { opacity: 0; animation: cp651bus 5.4s linear infinite 0s; }
      .b2 { opacity: 0; animation: cp651bus 5.4s linear infinite 1.8s; }
      .b3 { opacity: 0; animation: cp651bus 5.4s linear infinite 3.6s; }
    }
    @keyframes cp651bus { 0%, 5% { opacity: 0; } 12%, 90% { opacity: 1; } 96%, 100% { opacity: 0; } }
  </style>
  <text x="12" y="20" font-size="14" font-weight="bold" fill="#0f172a">Il est 20 h 12.</text>
  <rect x="12" y="32" width="150" height="26" rx="4" fill="none" stroke="#94a3b8" stroke-width="1.5"/>
  <text x="22" y="49" font-size="13" fill="#0f172a">Bus 121</text>
  <text x="152" y="49" text-anchor="end" font-size="13" fill="#0f172a">20 h 26</text>
  <rect x="12" y="62" width="150" height="26" rx="4" fill="none" stroke="#94a3b8" stroke-width="1.5"/>
  <text x="22" y="79" font-size="13" fill="#0f172a">Bus 57</text>
  <text x="152" y="79" text-anchor="end" font-size="13" fill="#0f172a">20 h 04</text>
  <rect x="12" y="92" width="150" height="26" rx="4" fill="none" stroke="#94a3b8" stroke-width="1.5"/>
  <text x="22" y="109" font-size="13" fill="#0f172a">Bus 94</text>
  <text x="152" y="109" text-anchor="end" font-size="13" fill="#0f172a">20 h 33</text>
  <rect x="12" y="122" width="150" height="26" rx="4" fill="none" stroke="#94a3b8" stroke-width="1.5"/>
  <text x="22" y="139" font-size="13" fill="#0f172a">Bus 260</text>
  <text x="152" y="139" text-anchor="end" font-size="13" fill="#0f172a">20 h 51</text>
  <g class="anim b1">
    <line x1="8" y1="75" x2="166" y2="75" stroke="#dc2626" stroke-width="2.5"/>
    <text x="172" y="79" font-size="12" fill="#dc2626">déjà parti</text>
  </g>
  <g class="anim b2">
    <rect x="9" y="29" width="156" height="32" rx="6" fill="none" stroke="#f97316" stroke-width="2.5"/>
    <text x="172" y="49" font-size="13" font-weight="bold" fill="#f97316">le prochain ✓</text>
  </g>
  <g class="anim b3">
    <text x="12" y="172" font-size="13" fill="#0f172a">Attente : de 20 h 12 à 20 h 26, <tspan font-weight="bold" fill="#f97316">14 min</tspan></text>
  </g>
</svg>`.trim();

// Calcul en chaîne animé : miroir exact des lignes de la page 2 — l'heure
// « illégale » 18 h 85 apparaît, se convertit, puis la 2e durée s'ajoute.
const SVG_ETAPES = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 148" width="290" height="143" role="img"
     aria-label="Calcul en chaîne : 18 h 50 plus 35 minutes donne 18 h 85 minutes, trop grand ; on enlève 60 minutes et on ajoute 1 heure : 19 h 25 ; puis 19 h 25 plus 1 heure 12 minutes donne 20 h 37, l'heure de fin">
  <style>
    .anim { opacity: 1; }
    @media (prefers-reduced-motion: no-preference) {
      .e1 { opacity: 0; animation: cp651et 5.4s linear infinite 0s; }
      .e2 { opacity: 0; animation: cp651et 5.4s linear infinite 1.8s; }
      .e3 { opacity: 0; animation: cp651et 5.4s linear infinite 3.6s; }
    }
    @keyframes cp651et { 0%, 5% { opacity: 0; } 12%, 90% { opacity: 1; } 96%, 100% { opacity: 0; } }
  </style>
  <rect x="12" y="14" width="64" height="28" rx="6" fill="none" stroke="#0f172a" stroke-width="2"/>
  <text x="44" y="33" text-anchor="middle" font-size="13.5" font-weight="bold" fill="#0f172a">18 h 50</text>
  <g class="anim e1">
    <line x1="80" y1="28" x2="130" y2="28" stroke="#f97316" stroke-width="2.5"/>
    <polygon points="138,28 128,23 128,33" fill="#f97316"/>
    <text x="109" y="18" text-anchor="middle" font-size="11.5" font-weight="bold" fill="#f97316">+ 35 min</text>
    <rect x="140" y="14" width="82" height="28" rx="6" fill="none" stroke="#fb923c" stroke-width="2.5"/>
    <text x="181" y="33" text-anchor="middle" font-size="13" font-weight="bold" fill="#fb923c">18 h 85 min</text>
    <text x="228" y="32" font-size="11.5" fill="#64748b">trop grand !</text>
  </g>
  <g class="anim e2">
    <path d="M 181 42 Q 165 88 66 100" fill="none" stroke="#f97316" stroke-width="2.5"/>
    <polygon points="58,102 68,96 69,106" fill="#f97316"/>
    <text x="192" y="62" font-size="11.5" font-weight="bold" fill="#f97316">on convertit :<tspan x="192" dy="18">+ 1 h, moins 60 min</tspan></text>
    <rect x="12" y="100" width="64" height="28" rx="6" fill="none" stroke="#f97316" stroke-width="2.5"/>
    <text x="44" y="119" text-anchor="middle" font-size="13.5" font-weight="bold" fill="#f97316">19 h 25</text>
  </g>
  <g class="anim e3">
    <line x1="80" y1="114" x2="130" y2="114" stroke="#f97316" stroke-width="2.5"/>
    <polygon points="138,114 128,109 128,119" fill="#f97316"/>
    <text x="109" y="140" text-anchor="middle" font-size="11" font-weight="bold" fill="#f97316">+ 1 h 12 min</text>
    <rect x="140" y="100" width="64" height="28" rx="6" fill="none" stroke="#0f172a" stroke-width="2"/>
    <text x="172" y="119" text-anchor="middle" font-size="13.5" font-weight="bold" fill="#0f172a">20 h 37</text>
    <text x="212" y="119" font-size="12" font-weight="bold" fill="#f97316">✓ heure de fin</text>
  </g>
</svg>`.trim();

const FICHE_ETAPES = {
    pages: [
        {
            titre: 'Avance étape par étape',
            lignes: [
                'Tu connais l\'heure de départ et la durée de chaque activité. Tu cherches l\'heure de fin.',
                'Ajoute les durées **une par une** : l\'heure trouvée après une activité devient le départ de la suivante.',
                'Attention : une heure = **60** minutes, pas 100.',
            ],
        },
        {
            titre: 'Un exemple',
            lignes: [
                'Départ : **18 h 50**. Une activité de **35 min**, puis une de **1 h 12 min**.',
            ],
            svg: SVG_ETAPES,
        },
    ],
};

const FICHE_BUS = {
    pages: [
        {
            titre: 'Lis le tableau',
            lignes: [
                'Repère d\'abord l\'heure qu\'il est dans l\'énoncé.',
                '**Barre** les bus déjà partis : leur heure de départ est avant l\'heure actuelle.',
                'Le prochain bus est celui qui part le **plus tôt** parmi ceux qui restent. Pour l\'attente, compte les minutes de l\'heure actuelle jusqu\'à son départ.',
            ],
        },
        {
            // SVG seul (revue 2026-08-01) : le schéma est autoporteur, son
            // en-tête « Il est 20 h 12 » pose le contexte.
            titre: 'Un exemple',
            svg: SVG_TABLEAU_BUS,
        },
    ],
};

// Filet pour les questions déjà en banque (sans sousType) : les deux méthodes
// en une page.
const FICHE_GENERALE = {
    pages: [
        {
            titre: 'Problèmes d\'horaires',
            lignes: [
                'Avance **étape par étape** depuis l\'heure de départ : ajoute les durées une par une.',
                'Avec un tableau d\'horaires : **barre** ce qui est déjà passé, le prochain est celui qui part le plus tôt parmi ceux qui restent.',
                'Attention : une heure = **60** minutes, pas 100.',
            ],
        },
    ],
};

const VARIANTES_PROBLEME = {
    variantes: [
        { sousType: 'etapes', pages: FICHE_ETAPES.pages },
        { sousType: 'bus', pages: FICHE_BUS.pages },
    ],
    defaut: FICHE_GENERALE,
};

const CP_651 = {
    probleme_horaire_qcm_unique: VARIANTES_PROBLEME,
    probleme_horaire_texte: VARIANTES_PROBLEME,
    probleme_horaire_texte_trou_saisie: FICHE_CONVERSION,
};

export default CP_651;
